import React, { useState, useEffect } from 'react';
import { serverPHP } from '../const/consts';
import { ConfirmModal } from '../composants/Modal';

const Administrateur = ({ user, setUser, phase, goNext, closeSession, redaction, setShowInfoAdmin }) => {
  const [contributions, setContributions] = useState([]);
  const [users, setUsers] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionDescription, setActionDescription] = useState('');

  const fetchCurrentContributions = async () => {
    try {
      const response = await fetch(`${serverPHP}getCurrentContributionsAndUsers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: user.currentsession,
          phase: phase - (redaction ? 0 : 1),
        }),
      });
      const data = await response.json();

      const activeUsersList = data.users || [];
      const bannedUsersList = data.banlist || [];

      setContributions(data.contributions || []);
      setUsers(activeUsersList);
      setBannedUsers(bannedUsersList);

    } catch (error) {
      console.error('Error fetching contributions:', error);
    }
  };

  useEffect(() => {
    fetchCurrentContributions();
    const intervalId = setInterval(fetchCurrentContributions, 5000);
    return () => clearInterval(intervalId);
  }, [user.currentsession, phase]);

  useEffect(() => {
    const allUsers = [...users, ...bannedUsers];
    if (allUsers.length > 0 && !allUsers.some(u => u.id == parseInt(selectedUser))) {
      setSelectedUser(allUsers[0].id);
    }
  }, [users, bannedUsers, selectedUser]);

  const showConfirmationDialog = (action, description) => {
    setPendingAction(() => action);
    setActionDescription(description);
    setShowConfirmModal(true);
  };

  const executeAction = () => {
    if (pendingAction) {
      pendingAction();
    }
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const handlePhaseSuivante = () => {
    showConfirmationDialog(
      () => {
        goNext(true);
        setShowInfoAdmin(false);
      },
      "forcer le passage à la phase suivante"
    );
  };

  const handleFinSession = () => {
    showConfirmationDialog(
      () => {
        closeSession();
        setShowInfoAdmin(false);
      },
      "forcer la fermeture de la session"
    );
  };

  const handleBanUser = () => {
    const userToManage = [...users, ...bannedUsers].find(u => u.id === parseInt(selectedUser));
    if (!userToManage) return;

    const isBanned = bannedUsers.some(u => u.id === parseInt(selectedUser));

    const action = async () => {
      // Sauvegarde de l'état précédent pour l'annulation
      const oldUsers = [...users];
      const oldBannedUsers = [...bannedUsers];

      // Mise à jour optimiste de l'UI
      if (isBanned) {
        setUsers([...users, userToManage]);
        setBannedUsers(bannedUsers.filter(u => u.id !== userToManage.id));
      } else {
        setUsers(users.filter(u => u.id !== userToManage.id));
        setBannedUsers([...bannedUsers, userToManage]);
      }

      try {
        const response = await fetch(`${serverPHP}${isBanned ? 'unbanUser.php' : 'banUser.php'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_id: user.id, user_id: selectedUser, session_id: user.currentsession }),
        });
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'L\'action a échoué');
        }
        // L'UI est déjà à jour, on peut resynchroniser pour être sûr
        fetchCurrentContributions();
      } catch (error) {
        console.error(`Error ${isBanned ? 'unbanning' : 'banning'} user:`, error);
        // Annulation de la mise à jour optimiste
        setUsers(oldUsers);
        setBannedUsers(oldBannedUsers);
      }
    };
    
    showConfirmationDialog(action, isBanned ? "réintégrer cet utilisateur" : "bannir cet utilisateur");
  };

  const handleDeleteContribution = () => {
    if (!selectedContribution) return;

    const action = async () => {
      const contributionIdToDelete = selectedContribution;
      
      // Sauvegarde de l'état précédent
      const oldContributions = [...contributions];
      
      // Mise à jour optimiste de l'UI
      setContributions(contributions.filter(c => c.id !== contributionIdToDelete));
      setSelectedContribution(null);

      try {
        const response = await fetch(`${serverPHP}deleteContribution.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_id: user.id, contribution_id: contributionIdToDelete, session_id: user.currentsession }),
        });
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'La suppression a échoué');
        }
        // L'UI est déjà à jour, on resynchronise
        fetchCurrentContributions();
      } catch (error) {
        console.error('Error deleting contribution:', error);
        // Annulation de la mise à jour optimiste
        setContributions(oldContributions);
        setSelectedContribution(contributionIdToDelete);
      }
    };
    
    showConfirmationDialog(action, "supprimer définitivement cette contribution");
  };

  const isSelectedUserBanned = bannedUsers.some(u => u.id === parseInt(selectedUser));
  const isOwnUser = selectedUser == user.id;

  const groupUsersByRole = (users) => ({
    redacteurs: users.filter(user => parseInt(user.role) === 0),
    evaluateurs: users.filter(user => parseInt(user.role) === 1),
    spectateurs: users.filter(user => parseInt(user.role) === 2)
  });

  const { redacteurs, evaluateurs, spectateurs } = groupUsersByRole(users);
  const showContributionsSection = phase > 0;

  return (
    <>
      <div className="admin-panel">
        <div className="admin-section">
          <h3 className="admin-section-title"><span className="section-icon">⚙️</span>Actions de session</h3>
          <div className="admin-actions-grid">
            <button onClick={handlePhaseSuivante} className="admin-button admin-button-primary">
              <span className="button-icon">⏭️</span><span className="button-text">{phase === 0 ? 'Démarrer la session' : 'Phase suivante'}</span>
            </button>
            <button onClick={handleFinSession} className="admin-button admin-button-danger">
              <span className="button-icon">🔚</span><span className="button-text">Terminer la session</span>
            </button>
          </div>
        </div>

        {showContributionsSection && (
          <div className="admin-section">
            <h3 className="admin-section-title">
              <span className="section-icon">📝</span>
              <span>{redaction ? 'Contributions actuelles' : 'Contributions de la phase précédente'}</span>
              <span className="contributions-count">({contributions.length})</span>
            </h3>
            <div className="contributions-management">
              <div className="form-group">
                <select size={6} value={selectedContribution || ''} onChange={(e) => setSelectedContribution(e.target.value)} className="contributions-select">
                  {contributions.length === 0 ? (
                    <option disabled>Aucune contribution disponible</option>
                  ) : (
                    contributions.map((contribution) => (
                      <option key={contribution.id} value={contribution.id}>
                        #{contribution.user_id} : {contribution.contenu.substring(0, 80)}{contribution.contenu.length > 80 ? '...' : ''}
                      </option>
                    ))
                  )}
                </select>
              </div>
              {redaction && (
                <div className="contributions-actions">
                  <button onClick={handleDeleteContribution} disabled={!selectedContribution} className={`admin-button admin-button-danger ${!selectedContribution ? 'disabled' : ''}`}>
                    <span className="button-icon">🗑️</span><span className="button-text">Supprimer</span>
                  </button>
                </div>
              )}
              {!redaction && contributions.length === 0 && (
                <div className="empty-state-small"><p className="empty-text">📋 Les contributions apparaîtront ici une fois la phase de rédaction terminée</p></div>
              )}
            </div>
          </div>
        )}

        <div className="admin-section">
          <h3 className="admin-section-title"><span className="section-icon">👥</span>Gestion des utilisateurs</h3>
          <div className="users-management">
            <div className="form-group">
              <label className="form-label">Sélectionner un utilisateur</label>
              <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className="users-select">
                {redacteurs.length > 0 && <optgroup label="🖊️ RÉDACTEURS">{redacteurs.map((u) => <option key={u.id} value={u.id}>#{u.id} - {u.name}</option>)}</optgroup>}
                {evaluateurs.length > 0 && <optgroup label="⭐ ÉVALUATEURS">{evaluateurs.map((u) => <option key={u.id} value={u.id}>#{u.id} - {u.name}</option>)}</optgroup>}
                {spectateurs.length > 0 && <optgroup label="👁️ SPECTATEURS">{spectateurs.map((u) => <option key={u.id} value={u.id}>#{u.id} - {u.name}</option>)}</optgroup>}
                {bannedUsers.length > 0 && <optgroup label="🚫 BANNIS">{bannedUsers.map((u) => <option key={u.id} value={u.id}>[BANNI] #{u.id} - {u.name}</option>)}</optgroup>}
              </select>
            </div>
            <div className="user-actions">
              {isOwnUser ? (
                <div className="self-user-notice"><span className="notice-icon">🚫</span><span className="notice-text">Impossible de se bannir soi-même</span></div>
              ) : (
                <button onClick={handleBanUser} className={`admin-button ${isSelectedUserBanned ? 'admin-button-success' : 'admin-button-danger'}`}>
                  <span className="button-icon">{isSelectedUserBanned ? '✅' : '🚫'}</span>
                  <span className="button-text">{isSelectedUserBanned ? 'Réintégrer' : 'Bannir cet utilisateur'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmModal title="⚠️ Confirmation requise" onClose={() => setShowConfirmModal(false)} onConfirm={executeAction} confirmText="Confirmer" cancelText="Annuler">
          <p>Voulez-vous vraiment <strong>{actionDescription}</strong> ?</p>
          <p className="text-sm text-secondary mt-2">Cette action ne peut pas être annulée.</p>
        </ConfirmModal>
      )}
    </>
  );
};

export default Administrateur;